import { ITreeNodeInstance } from '@infotech-soe/merps/types/Tree';
import { TDatabaseId } from '@infotech-soe/metaschema';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Map as ImmutableMap, Set as ImmutableSet } from 'immutable';

import { DatasetParamValues } from '../../infrastructure/providers/abstract/IApiProvider';
import {
  CategoryTreeStateEntity,
  DisableNodeStrategy,
} from '../../models/category/categoryTree';
import UniqueId from '../../services/uid/UniqueId';

import { failureAction, pendingAction, successAction } from './actionHandler';
import { CategoryTargetingInfo } from './category';
import { CategoryTree } from './dm-trees/CategoryTree';

export type HierarchyStateEntity = CategoryTreeStateEntity & {
  params: DatasetParamValues;
};
export type HierarchyState = Record<UniqueId, HierarchyStateEntity>;

const getTreeFor = (
  state: HierarchyState,
  categoryId: UniqueId
): CategoryTree => new CategoryTree(state[categoryId]);

const hierarchySlice = createSlice({
  name: 'hierarchy',
  initialState: {} as HierarchyState,
  reducers: {
    initializeTree(
      state,
      { payload: { categoryId } }: PayloadAction<{ categoryId: UniqueId }>
    ) {
      state[categoryId] = {
        categoryId,
        nodes: [],
        activeId: null,
        hierarchyRootId: null,
        checkedNodes: ImmutableSet(),
        expandedNodes: ImmutableSet(),
        pendingNodes: ImmutableSet(),
        childrenCache: ImmutableMap(),
        disabledQueue: ImmutableMap(),
        params: {},
      };
    },
    cloneTree(
      state,
      {
        payload: { categoryId, formId, targetId },
      }: PayloadAction<{
        categoryId: UniqueId;
        formId?: UniqueId;
        targetId: UniqueId;
      }>
    ) {
      const tree = (state[targetId] = {
        ...state[categoryId],
      });

      if (tree.activeId === null) {
        const rootNode = tree.nodes.find(({ parentId }) => parentId === null);
        if (rootNode) {
          tree.activeId = rootNode.id;
        }
      }

      tree.pendingNodes = ImmutableSet();
      tree.formId = formId;
      tree.hierarchyRootId = null;
    },
    destroyTree(
      state,
      { payload: { categoryId } }: PayloadAction<{ categoryId: UniqueId }>
    ) {
      delete state[categoryId];
    },
    loadRoots: pendingAction<
      HierarchyState,
      {
        categoryId: UniqueId;
        params?: DatasetParamValues;
        targetingInfo?: CategoryTargetingInfo;
        forceDOMFocus?: true;
      }
    >(),
    loadRootsSuccess: successAction<
      HierarchyState,
      {
        categoryId: UniqueId;
        nodes: ITreeNodeInstance[];
        params?: DatasetParamValues;
        targetingInfo?: CategoryTargetingInfo;
        forceDOMFocus?: true;
      }
    >((state, { payload: { categoryId, nodes, params } }) => {
      getTreeFor(state, categoryId).setRoots(nodes, params);
    }),
    loadRootsFailure: failureAction(),
    expandNode(
      state,
      {
        payload: { categoryId, nodeId },
      }: PayloadAction<{ categoryId: UniqueId; nodeId?: TDatabaseId }>
    ) {
      const tree = getTreeFor(state, categoryId);
      if (nodeId !== undefined) {
        tree.expandNode(nodeId);
      } else {
        tree.expandActiveNode();
      }
    },
    collapseNode(
      state,
      {
        payload: { categoryId, nodeId },
      }: PayloadAction<{ categoryId: UniqueId; nodeId?: TDatabaseId }>
    ) {
      const tree = getTreeFor(state, categoryId);
      if (nodeId !== undefined) {
        tree.collapseNode(nodeId);
      } else {
        tree.collapseActiveNode();
      }
    },
    markNodePending(
      state,
      {
        payload: { categoryId, nodeId },
      }: PayloadAction<{ categoryId: UniqueId; nodeId: TDatabaseId }>
    ) {
      getTreeFor(state, categoryId).markPending(nodeId);
    },
    unmarkNodePending(
      state,
      {
        payload: { categoryId, nodeId },
      }: PayloadAction<{ categoryId: UniqueId; nodeId: TDatabaseId }>
    ) {
      getTreeFor(state, categoryId).unmarkPending(nodeId);
    },
    loadChildren: pendingAction<
      HierarchyState,
      { categoryId: UniqueId; parentId: TDatabaseId }
    >(),
    loadChildrenSuccess: successAction<
      HierarchyState,
      {
        categoryId: UniqueId;
        parentId: TDatabaseId;
        nodes: ITreeNodeInstance[];
      }
    >((state, { payload: { categoryId, parentId, nodes } }) => {
      const tree = getTreeFor(state, categoryId);
      tree.setChildren(parentId, nodes);

      const { disabledQueue } = state[categoryId];
      if (disabledQueue) {
        disabledQueue.forEach(
          (strategy?: DisableNodeStrategy, nodeId?: TDatabaseId) => {
            tree.disableNode(nodeId ?? 0, strategy);
            disabledQueue.delete(nodeId ?? 0);
          }
        );
      }
    }),
    loadChildrenFailure: failureAction(),
    selectNode(
      state,
      {
        payload: { categoryId, nodeId },
      }: PayloadAction<{
        categoryId: UniqueId;
        nodeId: TDatabaseId;
        targetingInfo?: CategoryTargetingInfo;
        forceDOMFocus?: true;
        fullHierarchy?: boolean;
      }>
    ) {
      getTreeFor(state, categoryId).activateNode(nodeId);
    },
    toggleCheck: pendingAction<
      HierarchyState,
      { categoryId: UniqueId; nodeId?: TDatabaseId; shift?: boolean }
    >(),
    toggleCheckSuccess: successAction<
      HierarchyState,
      { categoryId: UniqueId; nodeId?: TDatabaseId; shift?: boolean }
    >(),
    toggleCheckFailure: failureAction(),
    toggleNodeChecked(
      state,
      {
        payload: { categoryId, parentId, toggledIds },
      }: PayloadAction<{
        categoryId: UniqueId;
        parentId: TDatabaseId;
        toggledIds: TDatabaseId[];
      }>
    ) {
      const tree = getTreeFor(state, categoryId);
      tree.toggleNodeChecks(parentId, toggledIds);
      tree.activateNode(toggledIds[0]);
    },
    nextNode(
      state,
      { payload: { categoryId } }: PayloadAction<{ categoryId: UniqueId }>
    ) {
      getTreeFor(state, categoryId).activateNext();
    },
    prevNode(
      state,
      { payload: { categoryId } }: PayloadAction<{ categoryId: UniqueId }>
    ) {
      getTreeFor(state, categoryId).activatePrevious();
    },
    setHierarchyRoot(
      state,
      {
        payload: { categoryId, nodeId },
      }: PayloadAction<{ categoryId: UniqueId; nodeId?: TDatabaseId }>
    ) {
      const tree = getTreeFor(state, categoryId);
      if (nodeId !== undefined) {
        tree.setHierarchyRoot(nodeId);
      } else {
        tree.setActiveAsHierarchyRoot();
      }
    },
    locateNode: pendingAction<
      HierarchyState,
      {
        categoryId: UniqueId;
        nodeId: TDatabaseId;
        targetingInfo?: CategoryTargetingInfo;
        forceDOMFocus?: true;
        params?: DatasetParamValues;
      }
    >(),
    locateNodeSuccess: successAction<
      HierarchyState,
      {
        categoryId: UniqueId;
        nodeId: TDatabaseId;
        targetingInfo?: CategoryTargetingInfo;
        forceDOMFocus?: true;
        params?: DatasetParamValues;
      } & {
        roots: ITreeNodeInstance[];
        path: Array<[TDatabaseId, ITreeNodeInstance[]]>;
        treeSelectionId: TDatabaseId;
      }
    >((state, { payload: { categoryId, nodeId, roots, path, params } }) => {
      const tree = getTreeFor(state, categoryId);
      tree.setRoots(roots, params);

      for (const [currentNodeId, children] of path) {
        tree.expandNode(currentNodeId);
        tree.setChildren(currentNodeId, children);
      }

      tree.activateNode(nodeId);
    }),
    locateNodeFailure: failureAction(),
    refresh: pendingAction<
      HierarchyState,
      {
        categoryId: UniqueId;
        params?: DatasetParamValues;
        loadPathToNode?: TDatabaseId;
      }
    >(),
    refreshSuccess: successAction<
      HierarchyState,
      {
        categoryId: UniqueId;
        hierarchyRootId?: TDatabaseId | null;
        isExpandedNode?: boolean;
      }
    >((state, { payload: { categoryId, hierarchyRootId, isExpandedNode } }) => {
      state[categoryId].refreshHierarchyRootId = hierarchyRootId;
      state[categoryId].refreshIsExpandedNode = isExpandedNode;
    }),
    refreshFailure: failureAction(),
    hierarchyToChecks: pendingAction<
      HierarchyState,
      { categoryId: UniqueId; excludedNodes: TDatabaseId[] }
    >(),
    hierarchyToChecksSuccess: successAction<
      HierarchyState,
      {
        categoryId: UniqueId;
        checkedNodes: TDatabaseId[];
        excludedNodes: TDatabaseId[];
      }
    >((state, { payload: { categoryId, checkedNodes } }) => {
      const tree = getTreeFor(state, categoryId);
      tree.unsetHierarchyRoot();
      tree.toggleNodeChecks(tree.getActiveNode().id, checkedNodes);
    }),
    hierarchyToChecksFailure: failureAction(),
    restoreChecks(
      state,
      {
        payload: { categoryId, nodeIds },
      }: PayloadAction<{ categoryId: UniqueId; nodeIds: TDatabaseId[] }>
    ) {
      const tree = getTreeFor(state, categoryId);
      nodeIds.forEach(nodeId => tree.setNodeChecked(nodeId));
    },
    clearChecked(
      state,
      { payload: { categoryId } }: PayloadAction<{ categoryId: UniqueId }>
    ) {
      getTreeFor(state, categoryId).clearChecksAndHierarchy();
    },
    disableNode(
      state,
      {
        payload: { categoryId, nodeId, strategy = DisableNodeStrategy.SINGLE },
      }: PayloadAction<{
        categoryId: UniqueId;
        nodeId: TDatabaseId;
        strategy?: DisableNodeStrategy;
      }>
    ) {
      const tree = getTreeFor(state, categoryId);

      if (tree.exists(nodeId)) {
        tree.disableNode(nodeId, strategy);
      } else {
        const { disabledQueue } = state[categoryId];

        if (disabledQueue) {
          state[categoryId].disabledQueue = disabledQueue.set(nodeId, strategy);
        }
      }
    },
    move: pendingAction<
      HierarchyState,
      { categoryId: UniqueId; moveIds: TDatabaseId[]; newParentId: TDatabaseId }
    >(),
    moveSuccess: successAction<
      HierarchyState,
      { categoryId: UniqueId; moveIds: TDatabaseId[] }
    >(),
    moveFailure: failureAction(),
  },
});

export const hierarchyReducer = hierarchySlice.reducer;

export default hierarchySlice.actions;
